import { Button } from 'ui/button/button.module';
import { Carousel } from 'ui/carousel/carousel.module';
import zenscroll from 'zenscroll';

import { LinkPreview } from 'components/linkPreview/linkPreview.module';
/* eslint-disable camelcase */
import CustomCard from 'components/nodes/CustomCard/customCard.module';
import FeedbackMessage from 'components/nodes/Feedback/feedback.module';
import FileUpload from 'components/nodes/FileUpload/fileUpload.module';
import Form from 'components/nodes/FormNode/form.module';
import { appendSkipButtonToRud, onRudFailure, onRudSuccess } from 'components/nodes/Rud/helpers/helpers';
import Rud from 'components/nodes/Rud/rud.module';
import SendAudio from 'components/nodes/SendAudio/sendAudio.module';
import SendDocument from 'components/nodes/SendDocument/sendDocument.module';
import SendImage from 'components/nodes/SendImage/sendImage.module';
import SendVideo from 'components/nodes/SendVideo/sendVideo.module';
import Slider from 'components/nodes/Slider/slider.module';
import Smo from 'components/nodes/Smo/smo.module';
import { StatusDivider } from 'components/statusDivider/statusDivider.module';
import { TextMessage } from 'components/textMessage/textMessage.module';
import { TimeStamp } from 'components/timeStamp/timeStamp.module';
import { IGNORE_TYPES_FOR_LS, LS_STORAGE_LIMIT, NOTIFICATION_AUDIO } from 'consts/consts';
import {
	BUTTON_TYPE,
	MESSAGE_SENDER,
	MESSAGE_TEXT_INPUT_TYPE,
	MESSAGE_TYPE,
	NODE_TYPES,
	POPOVER_ROLE,
	SCREEN_TYPE,
} from 'consts/enum';
import { LS } from 'consts/localStorage';
import {
	addClass,
	applyCode,
	changeElementStyleAttr,
	copyToClipboardEvent,
	hasClass,
	isDisableInputRequired,
	readFromLs,
	removeClass,
	writeToLs,
} from 'helpers/helpers';
import { emitMessage } from 'helpers/socket';

import { clearRudInputArea } from '../components/nodes/Rud/helpers/helpers';
import { FILE_TYPE } from '../consts/consts';
import { VOICE_MODE } from '../consts/enum';
import { checkIfAnchorTag, linkify, LOGGER, scrollHighlightToView } from '../helpers/helpers';
import { ENGT_CHAT } from './bot.model';
import { handleSessionTimer } from './session';

const { AGENT, BOT, USER } = MESSAGE_SENDER;
const INPUT_ENABLED_TYPES = [
	MESSAGE_TYPE.TEXT,
	MESSAGE_TYPE.OPTIONS,
	MESSAGE_TYPE.CAROUSEL,
	MESSAGE_TYPE.IMAGE,
	MESSAGE_TYPE.FILE,
	MESSAGE_TYPE.VIDEO,
	MESSAGE_TYPE.AUDIO,
	MESSAGE_TYPE.SKIP,
];

export const appendTimeStamp = (data = {}) => {
	appendToChat(data, { type: MESSAGE_TYPE.TIMESTAMP });
};

export const appendStatusDivider = (data = {}) => {
	if (document.getElementById('engtSessionTimeout')) {
		return;
	}
	appendToChat(data, { type: MESSAGE_TYPE.STATUS_DIVIDER });
};

export const appendInitTimeStamp = () => {
	const container = document.querySelector('#engtChatContent');
	ENGT_CHAT.set('config.isChatOpened', true);
	if (container && !container.hasChildNodes()) {
		appendTimeStamp({ firstStamp: true });
	}
};

let msgScroller;

export const scrollToLastMessage = async (isOldMessage = false, delayTime = 200) => {
	const chatContainer = document.getElementById('engtChatContent');

	addClass(document.querySelector('#engtChatWrapper > #engtScrollBtn'), 'engt-scrl-down-btn-hidden');

	ENGT_CHAT.set('manualScrollOnScreens', false);

	const updateManualScroll = () =>
		setTimeout(() => {
			ENGT_CHAT.set('manualScrollOnScreens', true);
		}, 1000);

	if (!msgScroller && chatContainer) {
		msgScroller = zenscroll.createScroller(chatContainer);
	}

	if (isOldMessage && !ENGT_CHAT.get('config.localStorageMessage')) {
		chatContainer.scrollTop = ENGT_CHAT.get('lastMsgScrollHeight');
		updateManualScroll();
		return;
	}

	msgScroller.stop();
	if (ENGT_CHAT.get('config.isFadingMessagesEnabled')) {
		msgScroller.toY(chatContainer.scrollHeight, 1000);
		updateManualScroll();
	} else {
		setTimeout(() => {
			msgScroller.toY(chatContainer.scrollHeight, 500);
			updateManualScroll();
		}, delayTime);
	}
};

const getMessageType = (chatData, configOptions) => {
	const type = chatData?.message?.type
		? chatData.message.type
		: chatData?.messagetype
		? chatData.messagetype
		: configOptions?.type || MESSAGE_TYPE.TEXT;
	return type === 'postback' || type === 'message_received' ? MESSAGE_TYPE.TEXT : type;
};

const disableReplyInputArea = (isOldMsg = false, isLatestOldMsg) => {
	// for old messages, we don't have to disable/enable for messages other than latest msg
	if (isOldMsg) {
		if (isLatestOldMsg) {
			ENGT_CHAT.get('disableAllInput')();
		}
	} else {
		ENGT_CHAT.get('disableAllInput')();
	}
	if (ENGT_CHAT.get('speech').speechRecognizing && ENGT_CHAT.get('inputMode') != VOICE_MODE.INPUT) {
		writeToLs(LS().STOP_AUDIO_RECORDING, true);
		ENGT_CHAT.set('speech.speechRecognizing', false);
		ENGT_CHAT.set('inputMode', VOICE_MODE.INPUT);
		ENGT_CHAT.get('speech').mediaRecorder.stop();
	}
};

const enableReplyInputArea = (isOldMsg = false, isLatestOldMsg) => {
	// for old messages, we don't have to disable/enable for messages other than latest msg
	if (isOldMsg) {
		if (isLatestOldMsg) {
			ENGT_CHAT.get('enableAllInput')();
		}
	} else {
		ENGT_CHAT.get('enableAllInput')();
	}
};

export const appendToChat = async (chatData, configOptions = { storeMsgInLs: true }, pushToAnimate = true) => {
	const { isOldMessage } = configOptions;
	const { isLatestOldMsg } = configOptions;
	let chatMessage = null;
	let storeMsg = configOptions.storeMsgInLs === undefined ? true : configOptions.storeMsgInLs;

	// If fading messages enabled, then they will be executed through a queue
	if (ENGT_CHAT.get('config.isFadingMessagesEnabled') && pushToAnimate) {
		if (!isOldMessage) {
			const queue = ENGT_CHAT.get('fadingMessageQueue');
			queue.push({
				data: chatData,
				config: configOptions,
			});
			ENGT_CHAT.set('fadingMessageQueue', queue);

			return;
		}
	}

	// isOldMessage will be true for LS stored messages and chat_history msgs
	if (isOldMessage) {
		const metaData = chatData.meta_data && JSON.parse(chatData.meta_data);
		if (!chatData.agent_name) {
			chatData.agent_name = metaData?.agent_name;
		}
		if (!chatData.agent_avatar) {
			chatData.agent_avatar = metaData?.agent_avatar;
		}
		if (chatData.sender === BOT && chatData?.agent_email) {
			chatData.sender = AGENT;
		}
	}

	const type = getMessageType(chatData, configOptions);

	if (
		INPUT_ENABLED_TYPES.includes(type) &&
		chatData?.source_node_type !== 'RCV_INP' &&
		chatData?.sender !== MESSAGE_SENDER.USER
	) {
		const rudInputArea = document.querySelector('rud-input-area');

		if (ENGT_CHAT.get('config.isRudRespondBoxEnabled') && rudInputArea && rudInputArea.innerHTML.trim() === '') {
			enableReplyInputArea(isOldMessage, isLatestOldMsg);
		}

		if (!ENGT_CHAT.get('config.isRudRespondBoxEnabled')) {
			enableReplyInputArea(isOldMessage, isLatestOldMsg);
		}
	}

	if (type === MESSAGE_TYPE.SKIP) {
		return;
	}

	switch (type) {
		case MESSAGE_TYPE.TEXT:
			{
				const isTextMessageStreaming = !!chatData.message.data?.user_query_id;
				if (isTextMessageStreaming) {
					chatData.message_id = chatData.message.data.user_query_id;
					configOptions.isStreamStarted = false;
					if (chatData.sender === BOT) {
						ENGT_CHAT.set('quotedMsgDetails', null);
						delete chatData['linked_event_id'];
						delete chatData['linked_response_data'];
					}

					const botResponseDiv = document.getElementById(chatData.message_id);

					if (botResponseDiv) {
						appendStreamToExistingChat(botResponseDiv, chatData);
						configOptions.isStreamStarted = true;
					} else {
						chatMessage = new TextMessage(chatData);
					}
				} else {
					const { message = {}, rud_message_id, message_id } = chatData;
					const { rcv_inp_type_flag: inputType } = message || {};

					if (chatData.sender === BOT) {
						ENGT_CHAT.set('quotedMsgDetails', null);
						delete chatData['linked_event_id'];
						delete chatData['linked_response_data'];
					}

					if (chatData?.source_node_type === 'RCV_INP') {
						ENGT_CHAT.set('isAllInputsDisabled', false);
						disableReplyInputArea(isOldMessage, isLatestOldMsg);
						if (chatData.error) {
							// validation failed flow
							onRudFailure(message, rud_message_id);
							scrollToLastMessage(false, 0);
						} else if (!(chatData.error ?? true)) {
							// success submit flow
							onRudSuccess(chatData);
							clearRudInputArea();
							scrollToLastMessage(false, 0);
						}
						// normal flow
						else {
							chatMessage = new Rud({ ...chatData, isOldMessage: isOldMessage && !isLatestOldMsg });
							const inputElement = document.getElementById(`rud-input-${message_id}`);
							if (inputElement) {
								inputElement.focus();
							}
						}
					} else {
						switch (inputType) {
							case MESSAGE_TEXT_INPUT_TYPE.DATE:
								clearRudInputArea(isOldMessage);
								break;
							case MESSAGE_TEXT_INPUT_TYPE.FILE:
								clearRudInputArea(isOldMessage);
								chatMessage = new FileUpload({
									...chatData,
									isOldMessage: isOldMessage && !isLatestOldMsg,
								});
								disableReplyInputArea(isOldMessage, isLatestOldMsg);
								break;
							case MESSAGE_TEXT_INPUT_TYPE.PASSWORD:
								break;
							default:
								chatMessage = new TextMessage(chatData);
						}
					}
				}
			}
			break;
		case MESSAGE_TYPE.OPTIONS:
			if (
				chatData?.source_node_type === NODE_TYPES.RCV_INP ||
				chatData?.source_node_name === NODE_TYPES.RCV_INP
			) {
				if (!isOldMessage) {
					appendSkipButtonToRud(chatData);
				}
			} else if (chatData?.source_node_name === NODE_TYPES.FEEDBACK_NODE) {
				clearRudInputArea(isOldMessage);
				chatMessage = new FeedbackMessage({ ...chatData, isOldMessage: isOldMessage && !isLatestOldMsg });
				if (chatData?.message?.enable_search === false) {
					disableReplyInputArea(isOldMessage, isLatestOldMsg);
				}
			} else {
				clearRudInputArea(isOldMessage);
				chatMessage = new Smo({ ...chatData, isOldMessage: isOldMessage && !isLatestOldMsg });
				if (Object.keys(chatData).includes('source_node_name')) {
					if (
						chatData?.message?.enable_search === false &&
						chatData?.source_node_name !== 'TRIGGER_LIVECHAT'
					) {
						disableReplyInputArea(isOldMessage, isLatestOldMsg);
					}
				} else if (ENGT_CHAT.get('config.blockSendMessageWithOptions')) {
					disableReplyInputArea(isOldMessage, isLatestOldMsg);
				}
			}
			break;
		case MESSAGE_TYPE.TIMESTAMP:
			clearRudInputArea(isOldMessage);
			chatMessage = new TimeStamp(chatData);
			storeMsg = false;
			break;
		case MESSAGE_TYPE.AGENT_PARTICIPATION_STATUS: {
			clearRudInputArea(isOldMessage);
			const msgData = chatData.message && chatData.message.data;
			const messageData =
				msgData && msgData.ENABLE_EVENT_HISTORY_NOTIFICATIONS ? `${msgData.PARTICIPATION_MESSAGE.trim()}` : '';
			const data = { status: messageData };
			chatMessage = msgData.ENABLE_EVENT_HISTORY_NOTIFICATIONS && new StatusDivider(data);
			storeMsg = false;
			break;
		}
		case MESSAGE_TYPE.CAROUSEL: {
			clearRudInputArea(isOldMessage);
			const { message_id, message, ...rest } = chatData;
			const defaultConfig = {
				layout: 'SHOW_ONE',
				type: 'SEAMLESS',
				orientation: 'VERTICAL',
			};

			const {
				carousel_cards = [],
				carousel_style_config: config = defaultConfig,
				footer_buttons: footerBtns = [],
				header_buttons: headerBtns = [],
			} = message?.data || {};

			const cards = carousel_cards?.length ? carousel_cards : message?.data || [];

			if (cards.length) {
				const { orientation, layout, type } = config || defaultConfig;

				const carouselType = `${orientation}_${layout}_${type}`.toUpperCase();
				const cardsToUse = cards.map(({ image_url, image_alt_text, ...rest }, index) => ({
					image: image_url,
					imageAlt: image_alt_text || `carousel_image_${index}`,
					...rest,
				}));

				chatMessage =
					cards?.length &&
					new Carousel({
						...rest,
						footerBtns,
						headerBtns,
						config: config || {},
						id: message_id,
						cards: cardsToUse,
						type: carouselType,
						popoverRole: POPOVER_ROLE.EXPANDABLE,
						ellipsis: {
							title: 2,
							description: 3,
						},
					});
			}
			break;
		}
		case MESSAGE_TYPE.STATUS_DIVIDER: {
			clearRudInputArea(isOldMessage);
			chatMessage = new StatusDivider(chatData);
			storeMsg = false;
			break;
		}
		case MESSAGE_TYPE.IMAGE: {
			clearRudInputArea(isOldMessage);
			chatMessage = new SendImage(chatData);
			break;
		}
		case MESSAGE_TYPE.FILE: {
			clearRudInputArea(isOldMessage);
			const { file_url } = chatData.message?.data;
			let fileType = 'file';
			if (file_url) {
				const fileExt = file_url.lastIndexOf('.') + 1;
				fileType = file_url.substring(fileExt);
			}

			if (chatData.message.type === FILE_TYPE.VIDEO || fileType === 'mp4') {
				chatMessage = new SendVideo(
					file_url
						? {
								...chatData,
								message: {
									...chatData.message,
									data: { ...chatData.message.data, video_url: file_url },
								},
						  }
						: { chatData }
				);
			} else if (chatData.message.type === FILE_TYPE.AUDIO || fileType === 'mp3') {
				chatMessage = new SendAudio(
					file_url
						? {
								...chatData,
								message: {
									...chatData.message,
									data: { ...chatData.message.data, audio_url: file_url },
								},
						  }
						: { chatData }
				);
			} else {
				chatMessage = new SendDocument(chatData);
			}
			break;
		}
		case MESSAGE_TYPE.VIDEO: {
			clearRudInputArea(isOldMessage);
			chatMessage = new SendVideo(chatData);
			break;
		}
		case MESSAGE_TYPE.AUDIO: {
			clearRudInputArea(isOldMessage);
			chatMessage = new SendAudio(chatData);
			break;
		}
		case MESSAGE_TYPE.AUTOCOMPLETE:
			clearRudInputArea(isOldMessage);
			fillAutoComplete(chatData);
			break;
		case MESSAGE_TYPE.SLIDER: {
			clearRudInputArea(isOldMessage);
			chatMessage = new Slider({ ...chatData, isOldMessage: isOldMessage && !isLatestOldMsg });
			disableReplyInputArea(isOldMessage, isLatestOldMsg);
			break;
		}
		case MESSAGE_TYPE.MAINTENANCE: {
			clearRudInputArea(isOldMessage);
			disableReplyInputArea(isOldMessage, isLatestOldMsg);
			chatMessage = new TextMessage({
				...chatData,
				sender: BOT,
			});
			break;
		}
		case MESSAGE_TYPE.COPY_TO_CLIPBOARD: {
			clearRudInputArea(isOldMessage);
			if (!isOldMessage) {
				copyToClipboardEvent(chatData?.['message']?.['data']);
			}
			storeMsg = false;
			break;
		}
		case MESSAGE_TYPE.CALL_REQUEST: {
			clearRudInputArea(isOldMessage);
			if (!isOldMessage) {
				applyCode(chatData?.['message']?.['data']?.['text']);
			}
			storeMsg = false;
			break;
		}
		case MESSAGE_TYPE.WEB_VIEW: {
			clearRudInputArea(isOldMessage);
			const { data } = chatData.message;
			const urlPrefixMessage = data['url_prefix_msg'];
			const url = data['url'];
			chatData.message.text = `<a href="${url}" target="_blank">${urlPrefixMessage}</a>`;
			chatMessage = new TextMessage(chatData);
			if (!isOldMessage && !document.hidden) {
				window.open(url, '_blank');
			}
			break;
		}
		case MESSAGE_TYPE.IDENTITY: {
			clearRudInputArea(isOldMessage);
			const { node_type } = chatData?.message?.data || {};
			if (chatData.validated) {
				if (node_type === NODE_TYPES.IDENTITY) {
					chatData.message.text = `Identity - ${chatData.message.data.title}`;
				} else {
					chatData.message.text = `Form - ${chatData.message.data.title}`;
				}
				chatMessage = new TextMessage(chatData);
				break;
			}
			if (node_type === NODE_TYPES.IDENTITY) {
				const auth_data = chatData.message.data;
				if (auth_data['user_id']) {
					ENGT_CHAT.set('currentUserId', ENGT_CHAT.get('status.sessionKey'));
					ENGT_CHAT.set('status.sessionKey', auth_data['user_id']);
				}
			}
			if (!chatData.message.data.hasOwnProperty('login_success')) {
				chatMessage = new Form({ ...chatData, isOldMessage: isOldMessage && !isLatestOldMsg });
				ENGT_CHAT.set('lastIdentityNode', chatData.message_id);
				disableReplyInputArea(isOldMessage, isLatestOldMsg);
			} else if (ENGT_CHAT.get('currentUserId') !== chatData.message.data['user_id']) {
				if (readFromLs(LS().NEW_SESSION)) {
					writeToLs(LS().NEW_SESSION, chatData.message.data['user_id']);
				}
			}
			break;
		}
		case MESSAGE_TYPE.FILE_RECEIVED: {
			clearRudInputArea(isOldMessage);
			chatData.message = JSON.parse(chatData.message);
			const { mimeType } = chatData.message;
			if (mimeType.indexOf('image') > -1) {
				chatData.message = {
					...chatData.message,
					data: {
						image_url: chatData?.message?.url,
						...chatData.message,
					},
				};
				chatMessage = new SendImage(chatData);
			} else {
				chatData.message = {
					...chatData.message,
					data: {
						file_url: chatData?.message?.url,
						...chatData.message,
					},
				};
				chatMessage = new SendDocument(chatData);
			}
			break;
		}
		case MESSAGE_TYPE.CUSTOM_CARD: {
			clearRudInputArea(isOldMessage);
			const msgData = chatData.message && chatData.message.data;
			chatMessage = new CustomCard({
				...chatData,
				customCardContent: msgData,
				isOldMessage: isOldMessage && !isLatestOldMsg,
			});
			break;
		}
		default:
			break;
	}

	if (chatMessage) {
		handleSessionTimer();
		if (!configOptions.isStreamStarted) {
			appendToContainer(chatMessage, isOldMessage);
		}
		hidePrevMsgAvatar(chatData, isOldMessage);
		changePrevMsgStyle(chatData, isOldMessage);
		if (!configOptions.isStreamStarted) {
			scrollToLastMessage(isOldMessage);
		}
		if (storeMsg && !isOldMessage) {
			addMessageToLS(chatData);
			updateUnreadMessage(chatData);
			updatePreviewForLaunchScreen();
		}
		if (chatData?.sender === USER) {
			if (ENGT_CHAT.get('config.edit_user_input')) {
				if (ENGT_CHAT.get('editFlowId') && ENGT_CHAT.get('isEditFlowUsed')) {
					if (document.getElementById(ENGT_CHAT.get('editFlowId'))) {
						document.getElementById(ENGT_CHAT.get('editFlowId')).style.display = 'none';
					}
					ENGT_CHAT.set('editFlowId', '');
				}
				ENGT_CHAT.set('isEditFlowUsed', true);
			}
			if (chatData.message) {
				const dataObj = {
					message: chatData.message,
					timestamp: chatData.timestamp,
					user: configOptions?.user || chatData.message.sender,
					sender: configOptions?.user || chatData.message.sender,
					message_id: chatData.message_id,
					type: chatData.message.type,
					msgDetails: configOptions?.msgDetails,
					broadcasted: true,
				};
				storeMsg && !configOptions?.isOldMessage && storeUserResponseInLs(dataObj);
			}

			resetInputArea();
		}
		setLastMessageDetails(chatData, isOldMessage);
	}
};

export const fillAutoComplete = (chatData) => {
	const { message = {} } = chatData;
	const { data = {} } = message;
	const { options = {} } = data;

	ENGT_CHAT.get('activeSmoDropdown')?.showAutocompleteOptions?.(options);
};

const addMessageToLS = (message) => {
	let messages = [];

	try {
		const messagesFromLs = readFromLs(LS().MESSAGE_MAP);
		messages = messagesFromLs ? (JSON.parse(messagesFromLs) && JSON.parse(messagesFromLs)?.data) || [] : [];
	} catch (e) {
		messages = [];
	}

	messages.push(message);

	writeToLs(LS().MESSAGE_MAP, JSON.stringify({ data: messages }));
};

const appendToContainer = (element, isOldMessage) => {
	const contentContainer = document.getElementById('engtChatContent');
	const domElement = element.render();
	isOldMessage ? contentContainer.prepend(domElement) : contentContainer.appendChild(domElement);
};

const hidePrevMsgAvatar = (currentMsg, isOldMessage = false) => {
	const lastMessage = ENGT_CHAT.get('lastMessageDetails');
	const chatHistoryFirstMessage = ENGT_CHAT.get('chatHistoryFirstMessageDetails');
	const avatarSelector = `[data-avatar-id="avatar-${isOldMessage ? currentMsg.message_id : lastMessage?.messageId}"]`;
	const avatarElement = document.querySelector(avatarSelector);

	if (isSenderBotOrAgent(currentMsg, isOldMessage ? chatHistoryFirstMessage : lastMessage)) {
		removeClass(avatarElement, 'engt-avatar-wrapper-animation');
		removeClass(avatarElement, 'engt-avatar-wrapper-animation-bot');
		setTimeout(() => {
			changeElementStyleAttr(avatarSelector, 'visibility', 'hidden');
		}, 800);
	}
};

const changePrevMsgStyle = (currentMsg, isOldMessage = false) => {
	const lastMessage = ENGT_CHAT.get('lastMessageDetails');
	const chatHistoryFirstMessage = ENGT_CHAT.get('chatHistoryFirstMessageDetails');
	const wrapperDiv = document.getElementById(isOldMessage ? currentMsg.message_id : lastMessage.messageId);
	const prevMessage = isOldMessage ? chatHistoryFirstMessage : lastMessage;

	if (wrapperDiv && isSameSender(currentMsg, prevMessage)) {
		removeClass(
			wrapperDiv.querySelector(`.engt-msg-border-${prevMessage?.sender}`),
			`engt-msg-border-${prevMessage?.sender}`
		);
	}
};

const isSenderBotOrAgent = (currentMsg, lastMessage) => {
	const { sender: currentSender, agent_name: currentAgentName } = currentMsg || {};
	const { sender: prevSender, agentName: prevAgentName } = lastMessage || {};

	return (
		isSenderBot(currentSender, prevSender) ||
		(isSenderAgent(currentSender, prevSender) && isSameAgent(prevAgentName, currentAgentName))
	);
};

const isSameSender = (currentMsg, lastMessage) => {
	const { sender: currentSender } = currentMsg || {};
	const { sender: prevSender } = lastMessage || {};

	return isSenderUser(currentSender, prevSender) || isSenderBotOrAgent(currentMsg, lastMessage);
};

const isSenderUser = (currentSender, prevSender) => currentSender === USER && prevSender === USER;

const isSenderBot = (currentSender, prevSender) => currentSender === BOT && prevSender === BOT;

const isSenderAgent = (currentSender, prevSender) => currentSender === AGENT && prevSender === AGENT;

const isSameAgent = (prevAgentName, currentAgentName) => prevAgentName === currentAgentName;

const resetInputArea = () => {
	const msgBox = document.getElementById('engtUserMessage');
	if (msgBox) {
		msgBox.innerText = '';
		msgBox.dispatchEvent(new Event('input', { bubbles: true }));
		document.getElementById('engtSendBtn')?.blur?.();
	}
};

const appendStreamToExistingChat = (botResponseDiv, chatData) => {
	const botResponseHTML = botResponseDiv.querySelector(`#engt-msg-${chatData.message_id}`);
	if (botResponseHTML) {
		const botResponseTextDiv = botResponseHTML.querySelector('.engt-msg-text');
		const linkifiedMessage = linkify(chatData.message.text);
		if (chatData.message.data.is_end_chunk) {
			const msgBeforeLinkify = chatData.message.text;
			if (checkIfAnchorTag(msgBeforeLinkify)) {
				addClass(botResponseTextDiv, 'engt-link');
			}
			botResponseTextDiv.style.display = 'inline';
			botResponseTextDiv.innerHTML += linkifiedMessage;

			if (ENGT_CHAT.get('config.isPdfLinkPreviewEnabled') || ENGT_CHAT.get('config.isWebLinkPreviewEnabled')) {
				const anchorTags = botResponseTextDiv.querySelectorAll('a');
				anchorTags.forEach((anchorTag) => {
					anchorTag.addEventListener('click', (event) => {
						const url = anchorTag.getAttribute('href');
						if (!url) return;

						const isPdfLink = url.includes('document/viewer.html');
						const isPdfPreviewEnabled = ENGT_CHAT.get('config.isPdfLinkPreviewEnabled') && isPdfLink;
						const isWebPreviewEnabled = ENGT_CHAT.get('config.isWebLinkPreviewEnabled') && !isPdfLink;

						if (isPdfPreviewEnabled || isWebPreviewEnabled) {
							event.preventDefault();
							ENGT_CHAT.get('onMaximizeScreen')();
							ENGT_CHAT.get('showSourcePreviewScreen')(url);
						}
					});
				});
			}

			scrollHighlightToView();
		} else {
			const isAnchorTag = checkIfAnchorTag(linkifiedMessage);
			if (isAnchorTag) {
				const getLinkPreview = () => {
					const linkPreview = new LinkPreview({
						msg: linkifiedMessage,
						message_id: chatData.message_id,
					});
					return linkPreview.render();
				};
				botResponseTextDiv.innerHTML += getLinkPreview();
			} else {
				botResponseTextDiv.innerHTML += linkifiedMessage;
			}
		}
	} else {
		LOGGER('Streaming div element not found');
	}
};

export const resetUnreadMessage = () => {
	ENGT_CHAT.set('unReadMessages', []);
	ENGT_CHAT.get('renderUnreadCount')(0);
};

const setLastMessageDetails = (data, isOldMessage = false) => {
	ENGT_CHAT.set(isOldMessage ? 'chatHistoryFirstMessageDetails' : 'lastMessageDetails', {
		sender: data.sender,
		agentName: data.agent_name,
		messageId: data.message_id,
	});
};

const updateUnreadMessage = (message) => {
	const unReadMessages = [...(ENGT_CHAT.get('unReadMessages') || [])] || [];
	const isChatclosed = hasClass(document.getElementById('engt-popup'), 'engt-popup-hidden');

	if (ENGT_CHAT.get('getCurrentScreen')() !== SCREEN_TYPE.CHAT || isChatclosed) {
		unReadMessages.push(message);
		ENGT_CHAT.set('unReadMessages', unReadMessages);
		ENGT_CHAT.get('renderUnreadCount')(unReadMessages.length);
	}
};

const updatePreviewForLaunchScreen = () => ENGT_CHAT.get('updatePreviewMessage')();

export const appendChatContent = (data) => {
	let chatContentLs = [];
	if (readFromLs(LS().PREV_MESSAGES)) {
		chatContentLs = JSON.parse(readFromLs(LS().PREV_MESSAGES));
	}
	Array.isArray(chatContentLs) ? null : (chatContentLs = []);
	let chatContentMapLS = {};
	if (readFromLs(LS().MESSAGE_MAP)) {
		chatContentMapLS = JSON.parse(readFromLs(LS().MESSAGE_MAP));
	}
	const messageTimestamp = data.timestamp;
	const sender = data.sender || data.message?.sender || data.user;
	if (sender) {
		if (sender === MESSAGE_SENDER.BOT || sender === MESSAGE_SENDER.AGENT) {
			const messageId = data.message_id;
			if (messageId && !chatContentMapLS[messageId]) {
				chatContentMapLS[messageId] = true;
				writeToLs(LS().MESSAGE_MAP, JSON.stringify(chatContentMapLS));
				const splittedTime = messageTimestamp.split(' ');
				const formattedTime = `${splittedTime[0]}T${splittedTime[1]}Z`;
				data.timestamp = new Date(formattedTime);
			}
		}

		const isTextMessageStreaming = !!data.message.data?.user_query_id;
		let isMsgExistsInLS = false;
		if (isTextMessageStreaming) {
			isMsgExistsInLS = appendStreamToExistingLS(data, chatContentLs, isMsgExistsInLS);
		}

		if (!isMsgExistsInLS) {
			chatContentLs.push(data);
		}
		if (chatContentLs.length === 1) {
			writeToLs('chatMessageTimestamp', new Date(messageTimestamp));
		}
		if (chatContentLs.length > LS_STORAGE_LIMIT) {
			const removedData = chatContentLs.shift();
			const msgSender = removedData.sender || removedData.message?.sender || removedData.user;
			if (msgSender && (msgSender === MESSAGE_SENDER.BOT || msgSender === MESSAGE_SENDER.AGENT)) {
				delete chatContentMapLS[removedData.message_id];
				writeToLs(LS().MESSAGE_MAP, JSON.stringify(chatContentMapLS));
			}
		}
		writeToLs(LS().PREV_MESSAGES, JSON.stringify(chatContentLs));
	}
};

const appendStreamToExistingLS = (data, chatContentLs, isMsgExistsInLS) => {
	data.message_id = data.message.data.user_query_id;
	for (let i = 0; i < chatContentLs.length; i++) {
		const prevMsg = chatContentLs[i];
		if (prevMsg.message_id === data.message_id) {
			prevMsg.message.text += data.message.text;
			isMsgExistsInLS = true;
			break;
		}
	}
	return isMsgExistsInLS;
};

const storeUserResponseInLs = (data) => {
	const { user, messagetype } = data;
	if (user === 'engt' || ENGT_CHAT.get('config.launch_flow_copy') || !ENGT_CHAT.get('config.storeUserResponseInLs')) {
		return;
	}
	appendChatContent(data);
};

const updateLiveChatStatus = (data) => {
	const { message } = data;
	const { is_enabled, is_live_chat_enabled } = message;
	const liveChatEnabled = is_live_chat_enabled && !is_enabled;

	if (liveChatEnabled) {
		writeToLs(LS().LIVECHAT, 'true');
		ENGT_CHAT.get('showUploadBtnForLiveChat')?.();
		ENGT_CHAT.get('showInputBox')?.();
	} else {
		writeToLs(LS().LIVECHAT, 'false');
		ENGT_CHAT.get('hideUploadBtnForLiveChat')?.();
		if (ENGT_CHAT.get('config.showReplyArea') === false) {
			ENGT_CHAT.get('hideInputBox')();
		}
	}

	ENGT_CHAT.get('reRenderHeaderOptions')();
};

const ignoreMessage = (msgType, data) => {
	if (msgType == MESSAGE_TYPE.UPDATE_LIVECHAT_STATUS) {
		updateLiveChatStatus(data);
	}
	const { source_node_type, message } = data;
	if (source_node_type === 'RCV_INP' && data.hasOwnProperty('error')) {
		return true;
	}
	if (IGNORE_TYPES_FOR_LS.indexOf(msgType) >= 0) {
		return true;
	}
	if (msgType === MESSAGE_TYPE.IDENTITY && message.hasOwnProperty('login_success')) {
		return true;
	}
	return false;
};

export const storeBotResponseInLs = (data) => {
	const dataObj = JSON.parse(JSON.stringify(data)) || {};
	const { message } = data;
	if (ENGT_CHAT.get('config.launch_flow_copy') || (message && message.type && ignoreMessage(message.type, data))) {
		if (data) {
			data.timestamp = new Date();
		}
		return;
	}
	data.timestamp = new Date();
	appendChatContent(dataObj);
};

const appendPreviousMessagesIntoDOM = (messages) => {
	ENGT_CHAT.set('config.isOldMessage', true);

	for (let i = messages.length - 1; i >= 0; i--) {
		if (messages[i].user && messages[i].user === 'guest') {
			appendToChat(messages[i], {
				user: messages[i].user,
				type: messages[i].type,
				msgDetails: messages[i].msgDetails,
				storeMsgInLs: false,
				isOldMessage: true,
				isLatestOldMsg: i === messages.length - 1,
			});
		} else {
			appendToChat(messages[i], { isOldMessage: true, isLatestOldMsg: i === messages.length - 1 });
		}
	}

	ENGT_CHAT.set('config.isOldMessage', false);
};
export const appendPreviousMessages = () => {
	if (ENGT_CHAT.get('config.launch_flow_copy')) {
		return;
	}
	if (ENGT_CHAT.get('config.loadChatHistory')) {
		writeToLs('chatMessageTimestamp', new Date());
		return;
	}

	const messages = JSON.parse(readFromLs(LS().PREV_MESSAGES)) || [];

	if (messages.length) {
		ENGT_CHAT.set('config.localStorageMessage', true);

		writeToLs('chatMessageTimestamp', new Date(messages[0].timestamp));
		appendPreviousMessagesIntoDOM(messages);
		if (isDisableInputRequired(messages[messages.length - 1])) {
			ENGT_CHAT.get('disableAllInput')();
		}

		ENGT_CHAT.set('config.localStorageMessage', false);
	}
};

export const chatHistoryPublish = (data) => {
	const { messages } = data;
	if (messages.length) {
		writeToLs('chatMessageTimestamp', new Date(messages[messages.length - 1].timestamp));
	}
	ENGT_CHAT.set('config.isOldMessage', true);
	ENGT_CHAT.set('config.localStorageMessage', false);
	for (let i = 0; i < messages.length; i++) {
		if (messages[i].sender === MESSAGE_SENDER.BOT) {
			// <TODO:khush> handle identity and feedback node cases
			appendToChat(messages[i], { isOldMessage: true });
		} else {
			// <TODO:khush> handle file recieved case
			appendToChat(messages[i], { isOldMessage: true });
		}
	}
	ENGT_CHAT.set('config.isOldMessage', false);

	if (ENGT_CHAT.get('config.temporaryRoomId')) {
		emitMessage('chat_history_received', { room: ENGT_CHAT.get('config.temporaryRoomId') });
		ENGT_CHAT.set('config.temporaryRoomId', null);
	}
};

export const generateStartConvButton = () => {
	const btn = document.createElement('div');
	btn.id = 'engtStartConversation';
	btn.style = `padding: 16px; background-color: ${ENGT_CHAT.get('config.textAreaColor')}; width: 100%;`;

	btn.appendChild(
		new Button({
			type: BUTTON_TYPE.PRIMARY,
			label: ENGT_CHAT.get('GENERIC_STRINGS')?.START_NEW_CONVERSATION || 'Start a new conversation',
			onClick: ENGT_CHAT.get('startNewConversation'),
			radius: 18,
		}).render()
	);

	return btn;
};

export const removeQuote = () => {
	const engtQuoteElement = document.getElementById('engtQuoteText');
	ENGT_CHAT.set('quotedMsgDetails', null);

	if (engtQuoteElement) {
		engtQuoteElement.innerHTML = '';
		engtQuoteElement.classList.remove('engt-quote-text');
		engtQuoteElement.classList.add('engt-hidden');
	}
};

export const playLiveChatNotfication = (data) => {
	let sendSoundNotification = false;
	const { type } = data.message;
	if (
		data.sender === 'agent' &&
		!data.message.hasOwnProperty('action') &&
		type != 'AGENT_PARTICIPATION_STATUS' &&
		type != 'CHANGE_AGENT_IMAGE_URL' &&
		type != 'UPDATE_LIVECHAT_STATUS' &&
		!ENGT_CHAT.get('infocus')
	) {
		sendSoundNotification = true;
		let notificationCount = ENGT_CHAT.get('notificationCount');
		notificationCount++;
		const title = document.title.split(')').pop();
		const newTitle = `(${ENGT_CHAT.get('notificationCount')})${title}`;
		document.title = newTitle;
		ENGT_CHAT.set('notificationCount', notificationCount);
	}
	if (!ENGT_CHAT.get('config.isNotified') && sendSoundNotification) {
		NOTIFICATION_AUDIO.play();
		ENGT_CHAT.set('config.isNotified', true);
		writeToLs(LS().IS_NOTIFED, true);
	}
};
